import React, {useEffect, useState} from 'react';
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap';
import {recordingRequest} from '../api/hls-recording';

const days = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const clone = value => JSON.parse(JSON.stringify(value));
const errors = {
  configuration_changed: 'La configurazione è stata modificata altrove. Ricarica prima di salvare.',
  device_upgrade_required: 'Questo NVR deve essere aggiornato prima di configurare la registrazione HLS.',
  invalid_recording_schedule: 'Controlla giorni, date e orari delle fasce.',
  invalid_recording_timezone: 'Fuso orario non valido. Esempio: Europe/Rome.',
  empty_recording_window: 'Inizio e fine non possono essere uguali. Per tutta la giornata usa 00:00–24:00.',
  duplicate_recording_exception: 'Ogni data può avere una sola eccezione.',
  field_not_found: 'Campo non disponibile o accesso non consentito.',
  recording_camera_not_found: 'Camera non disponibile per la registrazione HLS.'
};

function TimeWindow({value, onChange, onRemove, label}) {
  return <div className="d-flex flex-wrap align-items-center mb-2">
    <Form.Label className="mr-2 mb-0">Da</Form.Label>
    <Form.Control style={{width: 110}} className="mr-2" type="time" required
      aria-label={`${label} inizio`} value={value.start}
      onChange={e => onChange({...value, start: e.target.value})}/>
    <Form.Label className="mr-2 mb-0">a</Form.Label>
    <Form.Control style={{width: 110}} className="mr-2" type="text" required
      placeholder="24:00" pattern="(([01][0-9]|2[0-3]):[0-5][0-9]|24:00)"
      aria-label={`${label} fine`} value={value.end}
      onChange={e => onChange({...value, end: e.target.value})}/>
    <Button size="sm" variant="outline-danger" type="button" onClick={onRemove}
      aria-label={`Elimina ${label}`}>Elimina fascia</Button>
  </div>;
}

export default function PartnerHlsRecording({fieldId, token, onHide}) {
  const [catalog, setCatalog] = useState(null), [cameraKey, setCameraKey] = useState('');
  const [draft, setDraft] = useState(null), [baseRevision, setBaseRevision] = useState(null);
  const [error, setError] = useState(null), [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false), [saved, setSaved] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    setError(null); setCatalog(null); setDraft(null); setCameraKey('');
    recordingRequest(fieldId, token, {signal: abort.signal}).then(data => {
      if (abort.signal.aborted) return;
      setCatalog(data);
      if (data.cameras.length) {
        setCameraKey(data.cameras[0].key); setDraft(clone(data.cameras[0].schedule));
        setBaseRevision(data.configRevision);
      }
      setDirty(false); setSaved(false);
    }).catch(e => {if (!abort.signal.aborted) setError(e.message);});
    return () => abort.abort();
  }, [fieldId, token, reload]);

  useEffect(() => {
    if (!saved || !catalog || catalog.recordingInSync || catalog.configRevision !== baseRevision) return undefined;
    const abort = new AbortController();
    const timer = setTimeout(() => {
      recordingRequest(fieldId, token, {signal: abort.signal}).then(data => {
        if (!abort.signal.aborted) setCatalog(data);
      }).catch(e => {if (!abort.signal.aborted) setError(e.message);});
    }, 5000);
    return () => {clearTimeout(timer); abort.abort();};
  }, [saved, catalog, baseRevision, fieldId, token]);

  function discard() {return !dirty || window.confirm('Scartare le modifiche non salvate?');}
  function change(value) {setDraft(value); setDirty(true); setSaved(false);}
  function choose(key) {
    if (!discard()) return;
    setCameraKey(key); setDraft(clone(catalog.cameras.find(c => c.key === key).schedule));
    setBaseRevision(catalog.configRevision); setDirty(false); setSaved(false); setError(null);
  }
  function updateWeekly(index, value) {
    change({...draft, weekly: draft.weekly.map((w, i) => i === index ? value : w)});
  }
  function updateException(index, value) {
    change({...draft, exceptions: (draft.exceptions || []).map((w, i) => i === index ? value : w)});
  }
  async function save(event) {
    event.preventDefault(); setSaving(true); setError(null);
    try {
      const result = await recordingRequest(fieldId, token, {method: 'PUT',
        body: JSON.stringify({cameraKey, baseRevision, schedule: draft})});
      setBaseRevision(result.savedRevision); setDirty(false); setSaved(true);
      setCatalog(previous => ({...previous, ...result,
        cameras: previous.cameras.map(c => c.key === cameraKey ? {...c, schedule: clone(draft)} : c)}));
    } catch (e) {setError(e.message);}
    finally {setSaving(false);}
  }
  return <Modal show size="lg" backdrop="static" onHide={() => {if (!saving && discard()) onHide();}}>
    <Modal.Header closeButton={!saving}><Modal.Title>Registrazione HLS</Modal.Title></Modal.Header>
    <Form onSubmit={save}>
      <Modal.Body>
        {error && <Alert variant="danger">{errors[error] || 'Operazione non riuscita. Riprova senza chiudere le modifiche.'}</Alert>}
        {!catalog && !error && <Spinner animation="border" role="status"/>}
        {catalog && !catalog.available && <Alert variant="info">{errors.device_upgrade_required}</Alert>}
        {catalog && catalog.available && !catalog.cameras.length && <Alert variant="info">Nessuna camera fisica HLS configurata su questo campo.</Alert>}
        {catalog && catalog.available && draft && <>
          <p>Salva il flusso della camera fisica sul disco NVR, senza avviare Autocam. Puoi pianificare separatamente panoramiche e bullet. Le camere tracked usano l’archivio della panoramica associata.</p>
          {!catalog.online && <Alert variant="warning">NVR offline: puoi salvare il calendario; sarà applicato al ritorno del collegamento.</Alert>}
          {saved && <Alert variant={catalog.configRevision === baseRevision && catalog.recordingInSync ? 'success' : 'info'}>
            {catalog.configRevision !== baseRevision ? 'La configurazione salvata è stata sostituita da una revisione successiva. Ricarica per verificarla.'
              : catalog.recordingInSync ? 'Pianificazione confermata dal gestore di registrazione NVR.'
              : `Salvata sul server (revisione ${baseRevision}). In attesa di applicazione da parte del gestore NVR.`}
          </Alert>}
          <fieldset disabled={saving}>
            <Form.Group controlId="hls-camera"><Form.Label>Camera</Form.Label>
              <Form.Control as="select" value={cameraKey} onChange={e => choose(e.target.value)}>
                {catalog.cameras.map(c => <option key={c.key} value={c.key}>{c.name} ({c.backendCameraIds.join(', ')})</option>)}
              </Form.Control>
            </Form.Group>
            <Form.Check id="hls-enabled" label="Abilita registrazione pianificata" checked={draft.enabled}
              onChange={e => change({...draft, enabled: e.target.checked})}/>
            <Form.Group controlId="hls-timezone" className="mt-3"><Form.Label>Fuso orario del campo</Form.Label>
              <Form.Control value={draft.timezone} required maxLength={64}
                onChange={e => change({...draft, timezone: e.target.value})}/>
            </Form.Group>
            <h6>Fasce settimanali</h6>
            <p className="small">Fine esclusiva. Se la fine precede l’inizio, la fascia prosegue il giorno dopo. Per una giornata intera: 00:00–24:00.</p>
            {draft.weekly.map((window, index) => <div key={index} className="border rounded p-2 mb-2">
              <div className="mb-2">{days.map((day, i) => <Form.Check inline key={day} type="checkbox"
                id={`hls-week-${index}-${i}`} label={day} checked={window.weekdays.includes(i + 1)}
                onChange={e => updateWeekly(index, {...window, weekdays: e.target.checked
                  ? [...window.weekdays, i + 1].sort((a, b) => a - b) : window.weekdays.filter(d => d !== i + 1)})}/>)}</div>
              <TimeWindow label={`fascia ${index + 1}`} value={window} onChange={v => updateWeekly(index, v)}
                onRemove={() => change({...draft, weekly: draft.weekly.filter((_, i) => i !== index)})}/>
            </div>)}
            <Button size="sm" type="button" variant="outline-primary" disabled={draft.weekly.length >= 64}
              onClick={() => change({...draft, weekly: [...draft.weekly, {weekdays: [1, 2, 3, 4, 5, 6, 7], start: '08:00', end: '23:00'}]})}>Aggiungi fascia</Button>
            <h6 className="mt-4">Eccezioni per data</h6>
            <p className="small">Sostituiscono le fasce che iniziano in quella data. Nessuna fascia significa nessun avvio quel giorno; una registrazione iniziata il giorno prima prosegue fino alla sua fine.</p>
            {(draft.exceptions || []).map((exception, index) => <div key={index} className="border rounded p-2 mb-2">
              <Form.Control className="mb-2" type="date" required aria-label={`Data eccezione ${index + 1}`}
                value={exception.date} onChange={e => updateException(index, {...exception, date: e.target.value})}/>
              {exception.windows.map((window, wi) => <TimeWindow key={wi} label={`eccezione ${index + 1} fascia ${wi + 1}`} value={window}
                onChange={v => updateException(index, {...exception, windows: exception.windows.map((w, i) => i === wi ? v : w)})}
                onRemove={() => updateException(index, {...exception, windows: exception.windows.filter((_, i) => i !== wi)})}/>)}
              <Button type="button" size="sm" variant="outline-primary" disabled={exception.windows.length >= 16}
                onClick={() => updateException(index, {...exception, windows: [...exception.windows, {start: '08:00', end: '23:00'}]})}>Aggiungi fascia eccezione</Button>{' '}
              <Button type="button" size="sm" variant="outline-danger"
                onClick={() => change({...draft, exceptions: draft.exceptions.filter((_, i) => i !== index)})}>Elimina eccezione</Button>
            </div>)}
            <Button type="button" size="sm" variant="outline-primary" disabled={(draft.exceptions || []).length >= 366}
              onClick={() => change({...draft, exceptions: [...(draft.exceptions || []), {date: '', windows: []}]})}>Aggiungi eccezione</Button>
          </fieldset>
          <Alert variant="warning" className="mt-3 mb-0">La registrazione si arresta al limite di spazio configurato. Le fasce programmate non garantiscono che i video siano presenti: la disponibilità effettiva è un dato separato.</Alert>
        </>}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="light" disabled={saving} onClick={() => {if (discard()) setReload(v => v + 1);}}>Ricarica</Button>
        <Button type="button" variant="secondary" disabled={saving} onClick={() => {if (discard()) onHide();}}>Chiudi</Button>
        <Button type="submit" disabled={!draft || !dirty || saving}>{saving ? 'Salvataggio…' : 'Salva calendario'}</Button>
      </Modal.Footer>
    </Form>
  </Modal>;
}
